import React, { createContext, useContext, useEffect, useState } from 'react';
import { Home } from '../pages/Home';
import { Layout } from '../components/Layout';

// Router context
interface RouterContextType {
  pathname: string;
  navigate: (path: string) => void;
  params: Record<string, string>;
}

const RouterContext = createContext<RouterContextType>({
  pathname: '/',
  navigate: () => { },
  params: {},
});

export const useRouter = () => useContext(RouterContext);

// Route interface
export interface Route {
  path: string;
  component: React.ComponentType<any>;
  metaData?: () => Promise<{
    title: string;
    description: string;
    keywords?: string;
    type?: string;
    imageUrl?: string;
  }>;
}

// Server-side route matching
export const findRoute = (pathname: string, routes: Route[]): Route | undefined => {
  return routes.find(route => {
    // Convert route path to regex pattern
    const pattern = route.path
      .replace(/:[^/]+/g, '([^/]+)') // Replace :param with regex group
      .replace(/\*/g, '.*'); // Replace * with .*

    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });
};

// Extract params from pathname
export const extractParams = (pathname: string, route: Route): Record<string, string> => {
  const params: Record<string, string> = {};
  const pathParts = pathname.split('/');
  const routeParts = route.path.split('/');

  routeParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.slice(1);
      params[paramName] = pathParts[index] || '';
    }
  });

  return params;
};

// Router component for client-side navigation
interface RouterProps {
  routes: Route[];
  initialPathname: string;
  initialParams?: Record<string, string>;
}

export function Router({ routes, initialPathname, initialParams = {} }: RouterProps) {
  const [pathname, setPathname] = useState(initialPathname);
  const [params, setParams] = useState<Record<string, string>>(initialParams);

  // Navigation function
  const navigate = (path: string) => {
    const newRoute = findRoute(path, routes);
    if (newRoute) {
      const newParams = extractParams(path, newRoute);
      setPathname(path);
      setParams(newParams);
      window.history.pushState({}, '', path);
    }
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const newPath = window.location.pathname;
      const newRoute = findRoute(newPath, routes);
      if (newRoute) {
        const newParams = extractParams(newPath, newRoute);
        setPathname(newPath);
        setParams(newParams);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [routes]);

  // Get current route
  const currentRoute = findRoute(pathname, routes);

  // Update document title and meta tags on client
  useEffect(() => {
    if (currentRoute?.metaData) {
      currentRoute.metaData().then(metadata => {
        document.title = metadata.title;

        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', metadata.description);
        }

        // Update OpenGraph tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogTitle) ogTitle.setAttribute('content', metadata.title);
        if (ogDescription) ogDescription.setAttribute('content', metadata.description);
      });
    }
  }, [currentRoute]);

  // Render current route component
  const CurrentComponent = currentRoute?.component || Home;

  return (
    <RouterContext.Provider value={{ pathname, navigate, params }}>
      <Layout>
        <CurrentComponent />
      </Layout>
    </RouterContext.Provider>
  );
}
